import React, { useState, useEffect } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { JournalEntry, CreateJournalEntryDto, UpdateJournalEntryDto, CreateJournalEntryLineDto } from '../../api/journal';
import { Account } from '../../api/accounts';
import { formatCurrency, formatDateForInput } from '../../utils/formatters';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';
import AmountInput from '../ui/AmountInput';

interface JournalEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateJournalEntryDto | UpdateJournalEntryDto) => Promise<void>;
  onApprove?: (id: string) => Promise<void>;
  journalEntry?: JournalEntry | null;
  accounts: Account[];
  loading?: boolean;
  getNextEntryNumber?: () => Promise<string>;
}

interface FormLineItem {
  id: string;
  accountId: string;
  debitAmount: string;
  creditAmount: string;
  description: string;
}

interface FormData {
  date: string;
  description: string;
  reference: string;
  source: 'manual' | 'sales' | 'purchase' | 'payment' | 'system';
  lineItems: FormLineItem[];
  notes: string;
}

const SOURCE_OPTIONS = [
  { value: 'manual', label: 'Manual Entry' },
  { value: 'sales', label: 'Sales' },
  { value: 'purchase', label: 'Purchase' },
  { value: 'payment', label: 'Payment' },
  { value: 'system', label: 'System Generated' },
];

export default function JournalEntryModal({
  isOpen,
  onClose,
  onSubmit,
  onApprove,
  journalEntry,
  accounts,
  loading = false,
  getNextEntryNumber,
}: JournalEntryModalProps) {
  const [formData, setFormData] = useState<FormData>({
    date: new Date().toISOString().split('T')[0],
    description: '',
    reference: '',
    source: 'manual',
    lineItems: [
      { id: '1', accountId: '', debitAmount: '', creditAmount: '', description: '' },
      { id: '2', accountId: '', debitAmount: '', creditAmount: '', description: '' },
    ],
    notes: '',
  });

  const [nextEntryNumber, setNextEntryNumber] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isApproving, setIsApproving] = useState(false);

  const isEditing = !!journalEntry;
  const canEdit = !isEditing || journalEntry?.status === 'draft';

  useEffect(() => {
    if (isOpen && !isEditing && getNextEntryNumber) {
      getNextEntryNumber().then(setNextEntryNumber).catch(console.error);
    }
  }, [isOpen, isEditing, getNextEntryNumber]);

  useEffect(() => {
    if (journalEntry) {
      setFormData({
        date: formatDateForInput(journalEntry.date),
        description: journalEntry.description,
        reference: journalEntry.reference || '',
        source: journalEntry.source,
        lineItems: journalEntry.lineItems.map((item, index) => ({
          id: item.id || `${index + 1}`,
          accountId: item.accountId,
          debitAmount: item.debitAmount > 0 ? item.debitAmount.toString() : '',
          creditAmount: item.creditAmount > 0 ? item.creditAmount.toString() : '',
          description: item.description || '',
        })),
        notes: journalEntry.notes || '',
      });
    } else {
      setFormData({
        date: new Date().toISOString().split('T')[0],
        description: '',
        reference: '',
        source: 'manual',
        lineItems: [
          { id: '1', accountId: '', debitAmount: '', creditAmount: '', description: '' },
          { id: '2', accountId: '', debitAmount: '', creditAmount: '', description: '' },
        ],
        notes: '',
      });
    }
    setErrors({});
  }, [journalEntry, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    // Validate line items
    const validLineItems = formData.lineItems.filter(item => 
      item.accountId && (parseFloat(item.debitAmount) > 0 || parseFloat(item.creditAmount) > 0)
    );

    if (validLineItems.length < 2) {
      newErrors.lineItems = 'At least two line items are required';
    }

    // Check for double-entry bookkeeping balance
    const totalDebits = validLineItems.reduce((sum, item) => 
      sum + (parseFloat(item.debitAmount) || 0), 0
    );
    const totalCredits = validLineItems.reduce((sum, item) => 
      sum + (parseFloat(item.creditAmount) || 0), 0
    );

    if (Math.abs(totalDebits - totalCredits) > 0.01) {
      newErrors.balance = `Entry is not balanced. Debits: ${formatCurrency(totalDebits)}, Credits: ${formatCurrency(totalCredits)}`;
    }

    // Validate individual line items
    validLineItems.forEach((item, index) => {
      if (!item.accountId) {
        newErrors[`lineItem${index}Account`] = 'Account is required';
      }

      const debit = parseFloat(item.debitAmount) || 0;
      const credit = parseFloat(item.creditAmount) || 0;

      if (debit > 0 && credit > 0) {
        newErrors[`lineItem${index}Amount`] = 'Enter either debit or credit, not both';
      }

      if (debit === 0 && credit === 0) {
        newErrors[`lineItem${index}Amount`] = 'Enter either debit or credit amount';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleLineItemChange = (index: number, field: keyof FormLineItem, value: string) => {
    const newLineItems = [...formData.lineItems];
    newLineItems[index] = { ...newLineItems[index], [field]: value };
    setFormData(prev => ({ ...prev, lineItems: newLineItems }));

    // Clear related errors
    const errorKeys = [`lineItem${index}Account`, `lineItem${index}Amount`];
    if (errors.balance || errorKeys.some(key => errors[key])) {
      setErrors(prev => {
        const newErrors = { ...prev };
        errorKeys.forEach(key => delete newErrors[key]);
        delete newErrors.balance;
        return newErrors;
      });
    }
  };

  const addLineItem = () => {
    const newId = (formData.lineItems.length + 1).toString();
    setFormData(prev => ({
      ...prev,
      lineItems: [
        ...prev.lineItems,
        { id: newId, accountId: '', debitAmount: '', creditAmount: '', description: '' }
      ]
    }));
  };

  const removeLineItem = (index: number) => {
    if (formData.lineItems.length > 2) {
      setFormData(prev => ({
        ...prev,
        lineItems: prev.lineItems.filter((_, i) => i !== index)
      }));
    }
  };

  const getAccountName = (accountId: string): string => {
    const account = accounts.find(acc => acc.id === accountId);
    return account ? `${account.code} - ${account.name}` : '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const lineItems: CreateJournalEntryLineDto[] = formData.lineItems
        .filter(item => item.accountId && (parseFloat(item.debitAmount) > 0 || parseFloat(item.creditAmount) > 0))
        .map(item => ({
          accountId: item.accountId,
          debitAmount: parseFloat(item.debitAmount) || 0,
          creditAmount: parseFloat(item.creditAmount) || 0,
          description: item.description.trim() || undefined,
        }));

      const submitData = {
        date: formData.date,
        description: formData.description.trim(),
        reference: formData.reference.trim() || undefined,
        source: formData.source,
        lineItems,
        notes: formData.notes.trim() || undefined,
      };

      await onSubmit(submitData);
      onClose();
    } catch (error: any) {
      console.error('Error submitting journal entry:', error);
      toast.error(error.message || 'Failed to save journal entry');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApprove = async () => {
    if (!journalEntry?.id || !onApprove) return;

    setIsApproving(true);

    try {
      await onApprove(journalEntry.id);
      toast.success('Journal entry approved successfully');
      onClose();
    } catch (error: any) {
      console.error('Error approving journal entry:', error);
      toast.error(error.message || 'Failed to approve journal entry');
    } finally {
      setIsApproving(false);
    }
  };

  // Calculate totals
  const totalDebits = formData.lineItems.reduce((sum, item) => 
    sum + (parseFloat(item.debitAmount) || 0), 0
  );
  const totalCredits = formData.lineItems.reduce((sum, item) => 
    sum + (parseFloat(item.creditAmount) || 0), 0
  );
  const isBalanced = Math.abs(totalDebits - totalCredits) <= 0.01;

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-6xl">
      <BrandModalHeader
        title={
          isEditing
            ? `Edit Journal Entry: ${journalEntry?.entryNumber}`
            : `New Journal Entry${nextEntryNumber ? `: ${nextEntryNumber}` : ''}`
        }
        subtitle={
          isEditing
            ? (canEdit ? 'Update journal entry information below.' : 'This journal entry has been posted and cannot be edited.')
            : 'Create a new journal entry with proper double-entry bookkeeping.'
        }
        onClose={onClose}
      />
      <form onSubmit={handleSubmit}>
        <BrandModalBody>
                      {!canEdit && (
                        <div className="mb-4 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                          <p className="text-sm text-yellow-800">
                            Only draft entries can be edited. Posted entries must be reversed to make corrections.
                          </p>
                        </div>
                      )}

                    <div className="space-y-6">
                      {/* Header Information */}
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                        <div>
                          <label htmlFor="date" className="block text-sm font-medium text-slate-700">
                            Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            name="date"
                            id="date"
                            value={formData.date}
                            onChange={handleChange}
                            disabled={!canEdit}
                            className={`mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm ${
                              errors.date ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                            } ${!canEdit ? 'bg-slate-50/80' : ''}`}
                          />
                          {errors.date && <p className="mt-1 text-sm text-red-600">{errors.date}</p>}
                        </div>

                        <div>
                          <label htmlFor="reference" className="block text-sm font-medium text-slate-700">
                            Reference
                          </label>
                          <input
                            type="text"
                            name="reference"
                            id="reference"
                            value={formData.reference}
                            onChange={handleChange}
                            disabled={!canEdit}
                            className={`mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm ${!canEdit ? 'bg-slate-50/80' : ''}`}
                            placeholder="e.g., INV-2024-001"
                          />
                        </div>

                        <div>
                          <label htmlFor="source" className="block text-sm font-medium text-slate-700">
                            Source
                          </label>
                          <select
                            name="source"
                            id="source"
                            value={formData.source}
                            onChange={handleChange}
                            disabled={!canEdit}
                            className={`mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm ${!canEdit ? 'bg-slate-50/80' : ''}`}
                          >
                            {SOURCE_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label htmlFor="description" className="block text-sm font-medium text-slate-700">
                          Description <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="description"
                          id="description"
                          value={formData.description}
                          onChange={handleChange}
                          disabled={!canEdit}
                          className={`mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm ${
                            errors.description ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                          } ${!canEdit ? 'bg-slate-50/80' : ''}`}
                          placeholder="e.g., Bird sale - African Grey Parrots"
                        />
                        {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
                      </div>

                      {/* Line Items */}
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <label className="block text-sm font-medium text-slate-700">
                            Line Items <span className="text-red-500">*</span>
                          </label>
                          {canEdit && (
                            <button
                              type="button"
                              onClick={addLineItem}
                              className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                            >
                              <PlusIcon className="h-4 w-4 mr-1" />
                              Add Line
                            </button>
                          )}
                        </div>

                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-slate-50/80">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-1/3">
                                  Account
                                </th>
                                <th className="px-3 py-2 text-center text-xs font-medium text-slate-500 uppercase tracking-wider w-1/6">
                                  Debit
                                </th>
                                <th className="px-3 py-2 text-center text-xs font-medium text-slate-500 uppercase tracking-wider w-1/6">
                                  Credit
                                </th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-1/4">
                                  Description
                                </th>
                                {canEdit && (
                                  <th className="px-3 py-2 text-center text-xs font-medium text-slate-500 uppercase tracking-wider w-16">
                                    Action
                                  </th>
                                )}
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {formData.lineItems.map((item, index) => (
                                <tr key={item.id}>
                                  <td className="px-3 py-2">
                                    {canEdit ? (
                                      <select
                                        value={item.accountId}
                                        onChange={(e) => handleLineItemChange(index, 'accountId', e.target.value)}
                                        className={`block w-full input border-gray-300 shadow-sm sm:text-sm ${
                                          errors[`lineItem${index}Account`] ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                                        }`}
                                      >
                                        <option key="empty-option-1753461297273" value="">Select account...</option>
                                        {accounts.map(account => (
                                          <option key={account.id} value={account.id}>
                                            {account.code} - {account.name}
                                          </option>
                                        ))}
                                      </select>
                                    ) : (
                                      <span className="text-sm text-slate-900">
                                        {getAccountName(item.accountId)}
                                      </span>
                                    )}
                                    {errors[`lineItem${index}Account`] && (
                                      <p className="mt-1 text-xs text-red-600">{errors[`lineItem${index}Account`]}</p>
                                    )}
                                  </td>
                                  <td className="px-3 py-2">
                                    {canEdit ? (
                                      <AmountInput
                                        value={item.debitAmount}
                                        onChange={(v) => handleLineItemChange(index, 'debitAmount', v != null ? String(v) : '')}
                                        allowEmpty
                                        min={0}
                                        inputClassName={`block w-full input border-gray-300 shadow-sm sm:text-sm text-right ${
                                          errors[`lineItem${index}Amount`] ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                                        }`}
                                        placeholder="0.00"
                                      />
                                    ) : (
                                      <span className="text-sm text-slate-900 text-right block">
                                        {item.debitAmount ? formatCurrency(parseFloat(item.debitAmount)) : ''}
                                      </span>
                                    )}
                                    {errors[`lineItem${index}Amount`] && (
                                      <p className="mt-1 text-xs text-red-600">{errors[`lineItem${index}Amount`]}</p>
                                    )}
                                  </td>
                                  <td className="px-3 py-2">
                                    {canEdit ? (
                                      <AmountInput
                                        value={item.creditAmount}
                                        onChange={(v) => handleLineItemChange(index, 'creditAmount', v != null ? String(v) : '')}
                                        allowEmpty
                                        min={0}
                                        inputClassName="block w-full input border-gray-300 shadow-sm sm:text-sm text-right"
                                        placeholder="0.00"
                                      />
                                    ) : (
                                      <span className="text-sm text-slate-900 text-right block">
                                        {item.creditAmount ? formatCurrency(parseFloat(item.creditAmount)) : ''}
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-3 py-2">
                                    {canEdit ? (
                                      <input
                                        type="text"
                                        value={item.description}
                                        onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                                        className="block w-full input border-gray-300 shadow-sm sm:text-sm"
                                        placeholder="Optional description"
                                      />
                                    ) : (
                                      <span className="text-sm text-slate-900">
                                        {item.description}
                                      </span>
                                    )}
                                  </td>
                                  {canEdit && (
                                    <td className="px-3 py-2 text-center">
                                      {formData.lineItems.length > 2 && (
                                        <button
                                          type="button"
                                          onClick={() => removeLineItem(index)}
                                          className="text-red-600 hover:text-red-900"
                                        >
                                          <TrashIcon className="h-4 w-4" />
                                        </button>
                                      )}
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className="bg-slate-50/80">
                              <tr>
                                <td className="px-3 py-2 text-sm font-medium text-slate-900">
                                  Totals:
                                </td>
                                <td className="px-3 py-2 text-right text-sm font-medium text-slate-900">
                                  {formatCurrency(totalDebits)}
                                </td>
                                <td className="px-3 py-2 text-right text-sm font-medium text-slate-900">
                                  {formatCurrency(totalCredits)}
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    isBalanced ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                                  }`}>
                                    {isBalanced ? 'Balanced' : 'Unbalanced'}
                                  </span>
                                </td>
                                {canEdit && <td></td>}
                              </tr>
                            </tfoot>
                          </table>
                        </div>

                        {errors.lineItems && <p className="mt-2 text-sm text-red-600">{errors.lineItems}</p>}
                        {errors.balance && <p className="mt-2 text-sm text-red-600">{errors.balance}</p>}
                      </div>

                      {/* Notes */}
                      <div>
                        <label htmlFor="notes" className="block text-sm font-medium text-slate-700">
                          Notes
                        </label>
                        <textarea
                          name="notes"
                          id="notes"
                          rows={3}
                          value={formData.notes}
                          onChange={handleChange}
                          disabled={!canEdit}
                          className={`mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm ${!canEdit ? 'bg-slate-50/80' : ''}`}
                          placeholder="Optional notes about this journal entry"
                        />
                      </div>
                    </div>

        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={onClose}
          >
            {canEdit ? 'Cancel' : 'Close'}
          </button>
          {canEdit && (
            <button
              type="submit"
              disabled={isSubmitting || loading || isApproving}
              className="btn btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving...' : isEditing ? 'Update Entry' : 'Create Entry'}
            </button>
          )}
          {isEditing && journalEntry?.status === 'draft' && onApprove && (
            <button
              type="button"
              disabled={isApproving || loading}
              onClick={handleApprove}
              className="btn btn-primary text-sm bg-green-600 hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isApproving ? 'Approving...' : 'Approve & Post'}
            </button>
          )}
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
} 